import Button from "components/common/Button";
import CustomTable from "components/common/CustomTable";
import CustomTableLoader from "components/common/CustomTableLoader";
import Dropdown from "components/common/Dropdown";
import EmptyStateComp from "components/common/EmptyStateComp";
import SelectInput from "components/common/InputField/SelectInput";
import TextInput from "components/common/InputField/TextInput";
import Loader from "components/common/Loader";
import TabsContainer from "components/common/TabsContainer";
import { formatMoney, getCurrencyFromCurrencyCode } from "helpers";
import { displayProductChannelIcon } from "helpers/displayProductChannelIcon";
import { getCurrentStartAndEndDate } from "helpers/getCurrentStartAndEndDate";
import { useMergedSearchParams } from "helpers/mergeSearchParams";
import useGetSalesChannel from "hooks/Dashboard/useGetSalesChannel";
import useGetCustomerList from "hooks/salesHooks/useGetCustomerList";
import useGetSalesOrderList from "hooks/salesHooks/useGetSalesOrderList";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import useGetAllWarehouses from "hooks/useGetAllWarehouses";
import { debounce, truncate } from "lodash";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ModalContainer from "components/common/ModalContainer";
import { salesActions } from "redux/Ldb/actions";
import SynthTooltip from "components/common/SynthTooltip";
import useToggle from "hooks/useToggle";
import SalesOrderDelete from "../SalesOrderDelete";

type OrderListProps = {
    durationValue?: string;
    dateRange?: Date[];
    setShowSummary?: (show: boolean) => void;
    showSummary?: boolean;
    setSelectedOrders?: (orders: Record<string, any>[]) => void;
    setSalesChannel?: (salesChannel: string[]) => void;
};

const OrderList = ({
    durationValue,
    dateRange,
    setShowSummary,
    setSelectedOrders,
    setSalesChannel,
}: OrderListProps) => {
    const [param] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const currOrderTab = param.get("currOrderTab");
    const { updateSearchParams } = useMergedSearchParams();
    const [activeOrderTab, setActiveOrderTab] = useState<number>(0);
    const [updateStatusModal, setUpdateStatusModal] = useState(false);
    const [status, setStatus] = useState<string>("");
    const [salesTable, setSalesTable] = useState<string>("");
    const [channelFilter, setChannelFilter] = useState<string>(param.get("salesChannel") || "");
    const [customerName, setCustomerName] = useState<string>(param.get("customerName") || "");
    const [customerSearch, setCustomerSearch] = useState<string>("");
    const [allSelectedSales, setAllSelectedSales] = useState<Record<string, any>[]>([]);
    const [ongoingSelectedSales, setOngoingSelectedSales] = useState<Record<string, any>[]>([]);
    const [fulfilledSelectedSales, setFulfilledSelectedSales] = useState<Record<string, any>[]>([]);
    const [pendingSelectedSales, setPendingSelectedSales] = useState<Record<string, any>[]>([]);
    const [singleUpdate, setSingleUpdate] = useState<boolean>(false);
    const [updateCheck, setUpdateCheck] = useState<{ tab: number; channels: string[] }[]>([]);

    const [deleteSalesOrderModal, toggleSalesOrderModal] = useToggle(false);

    const tabs = ["All orders", "Ongoing orders", "Fulfilled orders", "Pending orders"];

    const {
        deleteSalesOrderSuccess,
        updatingMultipleSalesOrderStatus: isUpdating,
        updateMultipleSalesOrderStatusSuccess: updateSucces,
    } = useAppSelector((state) => state.sales);

    const {
        data: retailCustomerData,
        isFetching: fetchingRetailCustomers,
        setDebounceSearch,
    } = useGetCustomerList("retail");

    const {
        data: allWarehouses,
        warehouseValue,
        search: warehouseSearch,
        handleDebouncedChange: debounceChange,
        handleSearch: handleWarehouseSearch,
        handleFilterChange: handleWarehouseFilterChange,
        handleClearFilter: clearWarehouseFilter,
    } = useGetAllWarehouses();

    const {
        data: salesOrderData,
        isFetching: isFetchingSalesOrder,
        ongoingSalesOrder,
        fulfilledSalesOrder,
        pendingSalesOrder,
        setSalesOrder,
        setOngoingSalesOrder,
        setFulfilledSalesOrder,
        setPendingSalesOrder,
        isLoadingMore,
        pagination,
        ongoingPagination,
        pendingPagination,
        fulfilledPagination,
        updatedPage,
        setUpdatedPage,
        search,
        fulfilledSearch,
        ongoingSearch,
        pendingSearch,
        handleSearch,
        handleDebouncedChange,
        handleFilterChange,
        handleClearFilter,
        fulfilledWarehousefilter,
        ongoingWarehousefilter,
        pendingWarehousefilter,
        warehousefilter,
        setStartEndDate,
        saleType,
        setSaleType,
        customerEmail,
        setCustomerEmail,
        fetchSalesOrderListSuccess,
        refetch: refetchSalesOrderList,
    } = useGetSalesOrderList(
        activeOrderTab === 0
            ? ""
            : activeOrderTab === 1
            ? "ongoing"
            : activeOrderTab === 2
            ? "fulfilled"
            : "pending",
        channelFilter,
        updateSearchParams
    );
    const { data: salesChannel, isFetching: salesChannelLoading } = useGetSalesChannel();

    useEffect(() => {
        if (salesChannel) {
            setSalesChannel(salesChannel);
        }
    }, [salesChannel]);

    useEffect(() => {
        if (deleteSalesOrderSuccess) {
            const copyList = salesOrderData.filter((item) => {
                return !allSelectedSales.some((sale) => sale?._id === item?._id);
            });
            setSalesOrder(copyList);
            setAllSelectedSales([]);
            refetchSalesOrderList();
        }
    }, [deleteSalesOrderSuccess]);

    useEffect(() => {
        if (currOrderTab) {
            setActiveOrderTab(Number(currOrderTab));
        }
    }, [currOrderTab]);

    const channelCheck = (sales: Record<string, any>[]) => {
        const channels = [];
        sales?.forEach((item) => {
            if (
                !item?.channel?.toLowerCase()?.includes("synth") &&
                !channels.includes(item?.channel)
            ) {
                channels.push(item?.channel);
            }
        });
        return channels;
    };

    useEffect(() => {
        const copyArr = [...updateCheck];
        copyArr[activeOrderTab] = {
            tab: activeOrderTab,
            channels: channelCheck(
                activeOrderTab === 0
                    ? allSelectedSales
                    : activeOrderTab === 1
                    ? ongoingSelectedSales
                    : activeOrderTab === 2
                    ? fulfilledSelectedSales
                    : pendingSelectedSales
            ),
        };
        setUpdateCheck(copyArr);
    }, [
        ongoingSelectedSales,
        fulfilledSelectedSales,
        pendingSelectedSales,
        allSelectedSales,
        activeOrderTab,
    ]);

    const handleUpdateStatus = (state: string) => {
        const salesIds =
            state === "all"
                ? [...allSelectedSales]
                : state === "ongoing"
                ? [...ongoingSelectedSales]
                : state === "fulfilled"
                ? [...fulfilledSelectedSales]
                : [...pendingSelectedSales];

        setSalesTable(state);
        const body = salesIds?.map((item) => ({ saleId: item?._id, status: status }));
        dispatch(salesActions.updateMultipleSalesOrderStatus(body));
    };

    useEffect(() => {
        if (updateSucces) {
            setUpdateStatusModal(false);
            if (salesTable === "all") {
                const copyList = salesOrderData.map((item) => {
                    if (allSelectedSales.some((sale) => sale?._id === item?._id)) {
                        return { ...item, status: status };
                    }
                    return item;
                });
                setSalesOrder(copyList);
                setAllSelectedSales([]);
            } else if (salesTable === "ongoing") {
                const copyList = ongoingSalesOrder.filter((item) => {
                    return !ongoingSelectedSales.some((sale) => sale?._id === item?._id);
                });
                setOngoingSalesOrder(copyList);
                setOngoingSelectedSales([]);
            } else if (salesTable === "fulfilled") {
                const copyList = fulfilledSalesOrder.filter((item) => {
                    return !fulfilledSelectedSales.some((sale) => sale?._id === item?._id);
                });
                setFulfilledSalesOrder(copyList);
                setFulfilledSelectedSales([]);
            } else if (salesTable === "pending") {
                const copyList = pendingSalesOrder.filter((item) => {
                    return !pendingSelectedSales.some((sale) => sale?._id === item?._id);
                });
                setPendingSalesOrder(copyList);
                setPendingSelectedSales([]);
            }
            setStatus("");
            dispatch(salesActions.resetUpdateMultipleSalesOrderStatusSuccess());
        }
    }, [dispatch, updateSucces, salesTable]);

    useEffect(() => {
        if (allSelectedSales?.length > 0 && salesOrderData && activeOrderTab === 0) {
            const filteredSelected = salesOrderData.filter((item) =>
                allSelectedSales.some((sale) => sale?._id === item?._id)
            );
            if (filteredSelected.length !== allSelectedSales.length) {
                setAllSelectedSales(filteredSelected);
            }
        }
    }, [salesOrderData, activeOrderTab]);

    useEffect(() => {
        if (ongoingSelectedSales?.length > 0 && ongoingSalesOrder && activeOrderTab === 1) {
            const filteredSelected = ongoingSalesOrder.filter((item) =>
                ongoingSelectedSales.some((sale) => sale?._id === item?._id)
            );
            if (filteredSelected.length !== ongoingSelectedSales.length) {
                setOngoingSelectedSales(filteredSelected);
            }
        }
    }, [ongoingSalesOrder, activeOrderTab]);

    useEffect(() => {
        if (fulfilledSelectedSales?.length > 0 && fulfilledSalesOrder && activeOrderTab === 2) {
            const filteredSelected = fulfilledSalesOrder.filter((item) =>
                fulfilledSelectedSales.some((sale) => sale?._id === item?._id)
            );
            if (filteredSelected.length !== fulfilledSelectedSales.length) {
                setFulfilledSelectedSales(filteredSelected);
            }
        }
    }, [fulfilledSalesOrder, activeOrderTab]);

    useEffect(() => {
        if (pendingSelectedSales?.length > 0 && pendingSalesOrder && activeOrderTab === 3) {
            const filteredSelected = pendingSalesOrder.filter((item) =>
                pendingSelectedSales.some((sale) => sale?._id === item?._id)
            );
            if (filteredSelected.length !== pendingSelectedSales.length) {
                setPendingSelectedSales(filteredSelected);
            }
        }
    }, [pendingSalesOrder, activeOrderTab]);

    const handleSalesOrderCheckboxClick = (sale: Record<string, any>, status) => {
        const listCopy =
            status === "all"
                ? [...allSelectedSales]
                : status === "ongoing"
                ? [...ongoingSelectedSales]
                : status === "fulfilled"
                ? [...fulfilledSelectedSales]
                : [...pendingSelectedSales];
        const check = listCopy?.find((item) => item?._id === sale?._id);

        const newList = check
            ? listCopy?.filter((item) => item?._id !== sale?._id)
            : [...listCopy, sale];

        if (status === "all") {
            setAllSelectedSales(newList);
        } else if (status === "ongoing") {
            setOngoingSelectedSales(newList);
        } else if (status === "fulfilled") {
            setFulfilledSelectedSales(newList);
        } else if (status === "pending") {
            setPendingSelectedSales(newList);
        }
    };

    const allOrderTableHeader = [
        { title: "Order ID", widthClass: "w-[17%]" },
        { title: "No of products", widthClass: "w-[10]" },
        { title: "Sale source", widthClass: "w-[13.5%]" },
        { title: "Lead time", widthClass: "w-[13.5%]" },
        { title: "Estimated delivery date", widthClass: "w-[13.5%]" },
        { title: "Estimated revenue", widthClass: "w-[13.5%]" },
        { title: "Status", widthClass: "w-[13.5%]" },
        { title: "", widthClass: "w-[4.8%]" },
    ];

    const allOrderTableBody = salesOrderData?.map((order, idx) => {
        const isChecked = [...allSelectedSales].some((item) => {
            return item?._id === order?._id;
        });
        return [
            {
                content: (
                    <div
                        className={`py-3 pr-3 h-full border-slate-100 text-sm font-rocGroteskMedium text-slate-900`}
                    >
                        <span className="text-slate-600 text-[13px] block break-all">
                            SO-{truncate(order?.taId || order?.orderId, { length: 18 })}
                        </span>
                        <span className="text-slate-500 text-[10px] block ">
                            {moment(order?.saleDate)?.format("MM/DD/YYYY; hh:mm a")}
                        </span>
                    </div>
                ),

                cellClickAction: () => {
                    navigate(`/dashboard/order/${order?._id}`, {
                        state: { internalNavigation: true },
                    });
                },

                isItemChecked: isChecked,
                onCheckBoxClick: () => {
                    setSingleUpdate(false);
                    handleSalesOrderCheckboxClick(order, "all");
                },
                widthClass: "!pl-0",
            },
            {
                content: (
                    <div
                        className={`py-3  flex items-center h-full border-slate-100 text-sm font-rocGroteskMedium text-slate-900`}
                    >
                        {order?.totalProducts}
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm font-rocGroteskMedium text-slate-900`}
                    >
                        <div className="flex items-center space-x-2 ">
                            {order?.channel?.toLowerCase()?.includes("synth")
                                ? order?.customerDetails?.company ||
                                  (order?.customerDetails?.first_name &&
                                  order?.customerDetails?.last_name
                                      ? `${order?.customerDetails?.first_name} ${order?.customerDetails?.last_name}`
                                      : order?.customerDetails?.first_name ||
                                        order?.customerDetails?.last_name) ||
                                  order?.customerDetails?.name ||
                                  ""
                                : displayProductChannelIcon(order?.channel, "!w-6 !h-6")}

                            <span className="captalize ">
                                {order?.channel?.toLowerCase()?.includes("synth")
                                    ? ""
                                    : order?.channel?.toLowerCase()?.includes("trackstar")
                                    ? order?.channel?.split(".")[1]
                                    : order?.channel}
                            </span>
                        </div>
                    </div>
                ),
            },

            {
                content: (
                    <div
                        className={`py-3 pr-3 flex items-center h-full border-slate-100 text-sm font-rocGroteskMedium text-slate-900 `}
                    >
                        {Math.round(order?.leadTime)} days
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3  items-center h-full flex space-x-2 text-sm font-rocGroteskMedium text-slate-900 `}
                    >
                        {order?.deliveryDate
                            ? moment(order?.deliveryDate).format("MM/DD/YYYY")
                            : "N/A"}
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 flex items-center h-full  space-x-2 text-sm font-rocGroteskMedium text-slate-900 `}
                    >
                        {getCurrencyFromCurrencyCode(order?.currency)}
                        {formatMoney().format(order?.totalRevenue?.toFixed(2))}
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 flex items-center h-full  space-x-2 text-sm font-rocGroteskMedium text-slate-900 `}
                    >
                        <div
                            className={`w-fit h-[21px] flex items-center text-sm py-1 px-2  ${
                                order?.status === "fulfilled"
                                    ? "bg-g-25"
                                    : order?.status === "pending"
                                    ? "bg-[#FCC34E26]"
                                    : "bg-[#CCDDF380]"
                            } rounded-2xl`}
                        >
                            <span
                                className={`text-xs ${
                                    order?.status === "fulfilled"
                                        ? "text-g-60"
                                        : order?.status === "pending"
                                        ? "text-[#F4B000]"
                                        : "text-b-55"
                                }`}
                            >
                                {order?.status || "N/A"}
                            </span>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 flex items-center h-full  space-x-2 text-sm font-rocGroteskMedium text-slate-900 `}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Dropdown
                            value={""}
                            dropdown={<i className="ri-more-2-fill "></i>}
                            dropdownClassName={`shadow-cardShadow-4 -left-[140px]  border border-slate-100 !w-[159px] rounded !max-h-fit`}
                            dropdownContainerClasses={`shadow-cardShadow-4 -left-[140px]  border border-slate-100 !w-[159px] rounded !max-h-fit`}
                            name={"faq-dropdown"}
                            dropdownOptions={[
                                {
                                    label: "View details",
                                    value: "view",
                                    action: () =>
                                        navigate(`/dashboard/order/${order?._id}`, {
                                            state: { internalNavigation: true },
                                        }),
                                },
                                {
                                    label: "Edit order",
                                    value: "edit",
                                    action: () =>
                                        navigate(`/dashboard/order/${order?._id}/edit`, {
                                            state: { internalNavigation: true },
                                        }),
                                    disabled: order?.status?.toLowerCase() === "fulfilled",
                                },
                                {
                                    label: "Change order status",
                                    value: "view",
                                    action: () => {
                                        setAllSelectedSales([order]);
                                        setSingleUpdate(true);
                                        setUpdateStatusModal(true);
                                    },
                                    disabled: !order?.channel?.toLowerCase()?.includes("synth"),
                                },
                                {
                                    label: "Delete order",
                                    value: "delete",
                                    action: () => {
                                        setAllSelectedSales([order]);
                                        toggleSalesOrderModal();
                                    },
                                    labelClassName: "text-red-500",
                                },
                            ]}
                        />
                    </div>
                ),
            },
        ];
    });
    const ongoingOrderTableHeader = [
        { title: "Order ID", widthClass: "w-[17%]" },
        { title: "No of products", widthClass: "w-[10]" },
        { title: "Sale source", widthClass: "w-[13.5%]" },
        { title: "Lead time", widthClass: "w-[13.5%]" },
        { title: "Estimated delivery date", widthClass: "w-[13.5%]" },
        { title: "Estimated revenue", widthClass: "w-[13.5%]" },
        { title: "Status", widthClass: "w-[13.5%]" },
        { title: "", widthClass: "w-[4.8%]" },
    ];

    const ongoingOrderTableBody = ongoingSalesOrder?.map((order, idx) => {
        const isChecked = [...ongoingSelectedSales].some((item) => item?._id === order?._id);

        return [
            {
                content: (
                    <div
                        className={`py-3 pr-3 h-full border-slate-100 text-sm font-rocGroteskMedium text-slate-900`}
                    >
                        <span className="text-slate-600 text-[13px] block break-all">
                            SO-{truncate(order?.taId || order?.orderId, { length: 18 })}
                        </span>
                        <span className="text-slate-500 text-[10px] block">
                            {moment(order?.saleDate)?.format("MM/DD/YYYY; hh:mm a")}
                        </span>
                    </div>
                ),

                isItemChecked: isChecked,
                onCheckBoxClick: () => {
                    setSingleUpdate(false);
                    handleSalesOrderCheckboxClick(order, "ongoing");
                },
                cellClickAction: () => {
                    navigate(`/dashboard/order/${order?._id}`, {
                        state: { internalNavigation: true },
                    });
                },
                widthClass: "!pl-0",
            },
            {
                content: (
                    <div
                        className={`py-3  flex items-center h-full border-slate-100 text-sm font-rocGroteskMedium text-slate-900`}
                    >
                        {order?.totalProducts}
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm font-rocGroteskMedium text-slate-900`}
                    >
                        <div className="flex items-center space-x-2">
                            {order?.channel?.toLowerCase()?.includes("synth")
                                ? order?.customerDetails?.company || order?.customerDetails?.name
                                : displayProductChannelIcon(order?.channel, "!w-6 !h-6")}

                            <span className="captalize">
                                {order?.channel?.toLowerCase()?.includes("synth")
                                    ? ""
                                    : order?.channel?.toLowerCase()?.includes("trackstar")
                                    ? order?.channel?.split(".")[1]
                                    : order?.channel}
                            </span>
                        </div>
                    </div>
                ),
            },

            {
                content: (
                    <div
                        className={`py-3 pr-3 flex items-center h-full border-slate-100 text-sm font-rocGroteskMedium text-slate-900 `}
                    >
                        {Math.round(order?.leadTime)} days
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3  items-center h-full flex space-x-2 text-sm font-rocGroteskMedium text-slate-900 `}
                    >
                        {order?.deliveryDate
                            ? moment(order?.deliveryDate).format("MM/DD/YYYY")
                            : "N/A"}
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 flex items-center h-full  space-x-2 text-sm font-rocGroteskMedium text-slate-900 `}
                    >
                        {getCurrencyFromCurrencyCode(order?.currency)}
                        {formatMoney().format(order?.totalRevenue?.toFixed(2))}
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 flex items-center h-full  space-x-2 text-sm font-rocGroteskMedium text-slate-900 `}
                    >
                        <div
                            className={`w-fit h-[21px] flex items-center text-sm py-1 px-2 bg-[#CCDDF380] rounded-2xl`}
                        >
                            <span className={`text-xs text-b-55`}>{order?.status || "N/A"}</span>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 flex items-center h-full  space-x-2 text-sm font-rocGroteskMedium text-slate-900 `}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Dropdown
                            value={""}
                            dropdown={<i className="ri-more-2-fill "></i>}
                            dropdownClassName={`shadow-cardShadow-4 -left-[140px]  border border-slate-100 !w-[159px] rounded !max-h-fit`}
                            dropdownContainerClasses={`shadow-cardShadow-4 -left-[140px]  border border-slate-100 !w-[159px] rounded !max-h-fit`}
                            name={"faq-dropdown"}
                            dropdownOptions={[
                                {
                                    label: "View details",
                                    value: "view",
                                    action: () =>
                                        navigate(`/dashboard/order/${order?._id}`, {
                                            state: { internalNavigation: true },
                                        }),
                                },
                                {
                                    label: "Edit order",
                                    value: "edit",
                                    action: () =>
                                        navigate(`/dashboard/order/${order?._id}/edit`, {
                                            state: { internalNavigation: true },
                                        }),
                                    disabled: order?.status?.toLowerCase() === "fulfilled",
                                },
                                {
                                    label: "Change order status",
                                    value: "view",
                                    action: () => {
                                        setOngoingSelectedSales([order]);
                                        setSingleUpdate(true);
                                        setUpdateStatusModal(true);
                                    },
                                    disabled: !order?.channel?.toLowerCase()?.includes("synth"),
                                },
                            ]}
                        />
                    </div>
                ),
            },
        ];
    });

    const fulfilledOrderTableHeader = [
        { title: "Order ID", widthClass: "w-[17%]" },
        { title: "No of products", widthClass: "w-[10]" },
        { title: "Sale source", widthClass: "w-[13.5%]" },
        { title: "Lead time", widthClass: "w-[13.5%]" },
        { title: "Delivery date", widthClass: "w-[13.5%]" },
        { title: "Revenue", widthClass: "w-[13.5%]" },
        { title: "Status", widthClass: "w-[13.5%]" },
        { title: "", widthClass: "w-[4.8%]" },
    ];

    const fulfilledOrderTableBody = fulfilledSalesOrder?.map((order, idx) => {
        const isChecked = [...fulfilledSelectedSales].some((item) => item?._id === order?._id);

        return [
            {
                content: (
                    <div
                        className={`py-3 pr-3 h-full border-slate-100 text-sm font-rocGroteskMedium text-slate-900`}
                    >
                        <span className="text-slate-600 text-[13px] block break-all">
                            SO-{truncate(order?.taId || order?.orderId, { length: 18 })}
                        </span>
                        <span className="text-slate-500 text-[10px] block ">
                            {moment(order?.saleDate)?.format("MM/DD/YYYY; hh:mm a")}
                        </span>
                    </div>
                ),
                isItemChecked: isChecked,
                onCheckBoxClick: () => {
                    setSingleUpdate(false);
                    handleSalesOrderCheckboxClick(order, "fulfilled");
                },
                cellClickAction: () => {
                    navigate(`/dashboard/order/${order?._id}`, {
                        state: { internalNavigation: true },
                    });
                },
                widthClass: "!pl-0",
            },
            {
                content: (
                    <div
                        className={`py-3  flex items-center h-full border-slate-100 text-sm font-rocGroteskMedium text-slate-900`}
                    >
                        {order?.totalProducts}
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm font-rocGroteskMedium text-slate-900`}
                    >
                        <div className="flex items-center space-x-2">
                            {order?.channel?.toLowerCase()?.includes("synth")
                                ? order?.customerDetails?.company || order?.customerDetails?.name
                                : displayProductChannelIcon(order?.channel, "!w-6 !h-6")}

                            <span className="captalize">
                                {order?.channel?.toLowerCase()?.includes("synth")
                                    ? ""
                                    : order?.channel?.toLowerCase()?.includes("trackstar")
                                    ? order?.channel?.split(".")[1]
                                    : order?.channel}
                            </span>
                        </div>
                    </div>
                ),
            },

            {
                content: (
                    <div
                        className={`py-3 pr-3 flex items-center h-full border-slate-100 text-sm font-rocGroteskMedium text-slate-900 `}
                    >
                        {Math.round(order?.leadTime)} days
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3  items-center h-full flex space-x-2 text-sm font-rocGroteskMedium text-slate-900 `}
                    >
                        {order?.deliveryDate
                            ? moment(order?.deliveryDate).format("MM/DD/YYYY")
                            : "N/A"}
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 flex items-center h-full  space-x-2 text-sm font-rocGroteskMedium text-slate-900 `}
                    >
                        {getCurrencyFromCurrencyCode(order?.currency)}
                        {formatMoney().format(order?.totalRevenue?.toFixed(2))}
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 flex items-center h-full  space-x-2 text-sm font-rocGroteskMedium text-slate-900 `}
                    >
                        <div
                            className={`w-fit h-[21px] flex items-center text-sm py-1 px-2 bg-g-25 rounded-2xl`}
                        >
                            <span className={`text-xs text-g-60`}>{order?.status || "N/A"}</span>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 flex items-center h-full  space-x-2 text-sm font-rocGroteskMedium text-slate-900 `}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Dropdown
                            value={""}
                            dropdown={<i className="ri-more-2-fill "></i>}
                            dropdownClassName={`shadow-cardShadow-4 -left-[140px]  border border-slate-100 !w-[159px] rounded !max-h-fit`}
                            dropdownContainerClasses={`shadow-cardShadow-4 -left-[140px]  border border-slate-100 !w-[159px] rounded !max-h-fit`}
                            name={"faq-dropdown"}
                            dropdownOptions={[
                                {
                                    label: "Change order status",
                                    value: "view",
                                    action: () => {
                                        setFulfilledSelectedSales([order]);
                                        setSingleUpdate(true);
                                        setUpdateStatusModal(true);
                                    },
                                },
                            ]}
                        />
                    </div>
                ),
            },
        ];
    });

    const pendingOrderTableHeader = [
        { title: "Order ID", widthClass: "w-[17%]" },
        { title: "No of products", widthClass: "w-[10]" },
        { title: "Sale source", widthClass: "w-[13.5%]" },
        { title: "Lead time", widthClass: "w-[13.5%]" },
        { title: "Delivery date", widthClass: "w-[13.5%]" },
        { title: "Estimated revenue", widthClass: "w-[13.5%]" },
        { title: "Status", widthClass: "w-[13.5%]" },
        { title: "", widthClass: "w-[4.8%]" },
    ];

    const pendingOrderTableBody = pendingSalesOrder?.map((order, idx) => {
        const isChecked = [...pendingSelectedSales].some((item) => item?._id === order?._id);

        return [
            {
                content: (
                    <div
                        className={`py-3 !pl-0 pr-3 h-full border-slate-100 text-sm font-rocGroteskMedium text-slate-900`}
                    >
                        <span className="text-slate-600 text-[13px] block break-all">
                            SO-{truncate(order?.taId || order?.orderId, { length: 18 })}
                        </span>
                        <span className="text-slate-500 text-[10px] block">
                            {moment(order?.saleDate)?.format("MM/DD/YYYY; hh:mm a")}
                        </span>
                    </div>
                ),
                widthClass: "!pl-0",
                isItemChecked: isChecked,
                onCheckBoxClick: () => {
                    setSingleUpdate(false);
                    handleSalesOrderCheckboxClick(order, "pending");
                },
                cellClickAction: () => {
                    navigate(`/dashboard/order/${order?._id}`, {
                        state: { internalNavigation: true },
                    });
                },
            },
            {
                content: (
                    <div
                        className={`py-3  flex items-center h-full border-slate-100 text-sm font-rocGroteskMedium text-slate-900`}
                    >
                        {order?.totalProducts}
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm font-rocGroteskMedium text-slate-900`}
                    >
                        <div className="flex items-center space-x-2">
                            {order?.channel?.toLowerCase()?.includes("synth")
                                ? order?.customerDetails?.company || order?.customerDetails?.name
                                : displayProductChannelIcon(order?.channel, "!w-6 !h-6")}

                            <span className="captalize">
                                {order?.channel?.toLowerCase()?.includes("synth")
                                    ? ""
                                    : order?.channel?.toLowerCase()?.includes("trackstar")
                                    ? order?.channel?.split(".")[1]
                                    : order?.channel}
                            </span>
                        </div>
                    </div>
                ),
            },

            {
                content: (
                    <div
                        className={`py-3 pr-3 flex items-center h-full border-slate-100 text-sm font-rocGroteskMedium text-slate-900 `}
                    >
                        {Math.round(order?.leadTime)} days
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3  items-center h-full flex space-x-2 text-sm font-rocGroteskMedium text-slate-900 `}
                    >
                        {order?.deliveryDate
                            ? moment(order?.deliveryDate).format("MM/DD/YYYY")
                            : "N/A"}
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 flex items-center h-full  space-x-2 text-sm font-rocGroteskMedium text-slate-900 `}
                    >
                        {getCurrencyFromCurrencyCode(order?.currency)}
                        {formatMoney().format(order?.totalRevenue?.toFixed(2))}
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 flex items-center h-full  space-x-2 text-sm font-rocGroteskMedium text-slate-900 `}
                    >
                        <div
                            className={`w-fit h-[21px] flex items-center text-sm py-1 px-2 bg-[#FCC34E26] rounded-2xl`}
                        >
                            <span className={`text-xs text-[#F4B000]`}>
                                {order?.status || "N/A"}
                            </span>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 flex items-center h-full  space-x-2 text-sm font-rocGroteskMedium text-slate-900 `}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Dropdown
                            value={""}
                            dropdown={<i className="ri-more-2-fill "></i>}
                            dropdownClassName={`shadow-cardShadow-4 -left-[140px]  border border-slate-100 !w-[159px] rounded !max-h-fit`}
                            dropdownContainerClasses={`shadow-cardShadow-4 -left-[140px]  border border-slate-100 !w-[159px] rounded !max-h-fit`}
                            name={"faq-dropdown"}
                            dropdownOptions={[
                                {
                                    label: "View details",
                                    value: "view",
                                    action: () =>
                                        navigate(`/dashboard/order/${order?._id}`, {
                                            state: { internalNavigation: true },
                                        }),
                                },
                                {
                                    label: "Edit order",
                                    value: "edit",
                                    action: () =>
                                        navigate(`/dashboard/order/${order?._id}/edit`, {
                                            state: { internalNavigation: true },
                                        }),
                                    disabled: order?.status?.toLowerCase() === "fulfilled",
                                },
                                {
                                    label: "Change order status",
                                    value: "view",
                                    action: () => {
                                        setPendingSelectedSales([order]);
                                        setSingleUpdate(true);
                                        setUpdateStatusModal(true);
                                    },
                                    disabled: !order?.channel?.toLowerCase()?.includes("synth"),
                                },
                            ]}
                        />
                    </div>
                ),
            },
        ];
    });

    const displayActiveOrderTab = () => {
        switch (activeOrderTab) {
            case 0:
                return isFetchingSalesOrder && Number(updatedPage) === 1 ? (
                    <CustomTableLoader tableHeader={allOrderTableHeader} />
                ) : salesOrderData?.length > 0 ? (
                    <div>
                        <CustomTable
                            tableBody={allOrderTableBody}
                            tableHeader={allOrderTableHeader}
                            isAllSelectable={false}
                            isCellSelectable={true}
                            isCellBordered={true}
                            headerContainerClass="!bg-slate-50  !border-slate-100 "
                            bodyItemClass="hover:border-slate-100 !border-b hover:bg-slate-50 "
                            headerItemClass="font-rocGroteskMedium !text-slate-700"
                            tableContainerClass="pb-[90px] mb-[-90px]"
                        />
                        {isLoadingMore && (
                            <div className="flex justify-center my-4">
                                <Loader color="gm-25" size={4} />
                            </div>
                        )}

                        {pagination?.currentPage === pagination?.noOfPages && (
                            <div className="flex my-4 justify-center text-slate-500 text-sm font-rocGroteskMedium">
                                End of list
                            </div>
                        )}
                    </div>
                ) : (
                    <EmptyStateComp
                        title="No sales order data available"
                        description="There are no sales order data available for this section"
                        contentContainerClass="!w-fit"
                        icon={<i className="ri-box-3-line text-slate-500 text-[40px]"></i>}
                    />
                );

            case 1:
                return isFetchingSalesOrder && Number(updatedPage) === 1 ? (
                    <CustomTableLoader tableHeader={ongoingOrderTableHeader} />
                ) : ongoingSalesOrder?.length > 0 ? (
                    <div>
                        <CustomTable
                            tableBody={ongoingOrderTableBody}
                            tableHeader={ongoingOrderTableHeader}
                            isAllSelectable={false}
                            isCellSelectable={true}
                            isCellBordered={true}
                            headerContainerClass="!bg-slate-50  !border-slate-100 "
                            bodyItemClass="hover:border-slate-100 !border-b hover:bg-slate-50 "
                            headerItemClass="font-rocGroteskMedium !text-slate-700"
                            tableContainerClass="pb-[90px] mb-[-90px]"
                        />

                        {isLoadingMore && (
                            <div className="flex justify-center my-4">
                                <Loader color="gm-25" size={4} />
                            </div>
                        )}

                        {ongoingPagination?.currentPage === ongoingPagination?.noOfPages && (
                            <div className="flex my-4 justify-center text-slate-500 text-sm font-rocGroteskMedium">
                                End of list
                            </div>
                        )}
                    </div>
                ) : (
                    <EmptyStateComp
                        title="No sales order data available"
                        description="There are no sales order data available for this section"
                        contentContainerClass="!w-fit"
                        icon={<i className="ri-box-3-line text-slate-500 text-[40px]"></i>}
                    />
                );

            case 2:
                return isFetchingSalesOrder && Number(updatedPage) === 1 ? (
                    <CustomTableLoader tableHeader={fulfilledOrderTableHeader} />
                ) : fulfilledSalesOrder?.length > 0 ? (
                    <div>
                        <CustomTable
                            tableBody={fulfilledOrderTableBody}
                            tableHeader={fulfilledOrderTableHeader}
                            isAllSelectable={false}
                            isCellSelectable={true}
                            isCellBordered={true}
                            headerContainerClass="!bg-slate-50  !border-slate-100 "
                            bodyItemClass="hover:border-slate-100 !border-b hover:bg-slate-50 "
                            headerItemClass="font-rocGroteskMedium !text-slate-700"
                        />

                        {isLoadingMore && (
                            <div className="flex justify-center my-4">
                                <Loader color="gm-25" size={4} />
                            </div>
                        )}

                        {fulfilledPagination?.currentPage === fulfilledPagination?.noOfPages && (
                            <div className="flex my-4 justify-center text-slate-500 text-sm font-rocGroteskMedium">
                                End of list
                            </div>
                        )}
                    </div>
                ) : (
                    <EmptyStateComp
                        title="No sales order data available"
                        description="There are no sales order data available for this section"
                        contentContainerClass="!w-fit"
                        icon={<i className="ri-box-3-line text-slate-500 text-[40px]"></i>}
                    />
                );

            case 3:
                return isFetchingSalesOrder && Number(updatedPage) === 1 ? (
                    <CustomTableLoader tableHeader={fulfilledOrderTableHeader} />
                ) : pendingSalesOrder?.length > 0 ? (
                    <div>
                        <CustomTable
                            tableBody={pendingOrderTableBody}
                            tableHeader={pendingOrderTableHeader}
                            isCellSelectable={true}
                            isCellBordered={true}
                            headerContainerClass="!bg-slate-50  !border-slate-100 "
                            bodyItemClass="hover:border-slate-100 !border-b hover:bg-slate-50 "
                            headerItemClass="font-rocGroteskMedium !text-slate-700"
                            tableContainerClass="pb-[90px] mb-[-90px]"
                        />

                        {isLoadingMore && (
                            <div className="flex justify-center my-4">
                                <Loader color="gm-25" size={4} />
                            </div>
                        )}

                        {pendingPagination?.currentPage === pendingPagination?.noOfPages && (
                            <div className="flex my-4 justify-center text-slate-500 text-sm font-rocGroteskMedium">
                                End of list
                            </div>
                        )}
                    </div>
                ) : (
                    <EmptyStateComp
                        title="No sales order data available"
                        description="There are no sales order data available for this section"
                        contentContainerClass="!w-fit"
                        icon={<i className="ri-box-3-line text-slate-500 text-[40px]"></i>}
                    />
                );

            default:
                return (
                    <div>
                        <CustomTable
                            tableBody={allOrderTableBody}
                            tableHeader={allOrderTableHeader}
                            isCellSelectable={true}
                            isCellBordered={false}
                            headerContainerClass="!bg-slate-50  !border-slate-100 "
                            bodyItemClass="hover:border-slate-100 !border-b hover:bg-slate-50 "
                            headerItemClass="font-rocGroteskMedium !text-slate-700"
                            tableContainerClass="pb-[90px] mb-[-90px]"
                        />
                    </div>
                );
        }
    };

    useEffect(() => {
        setStartEndDate(
            getCurrentStartAndEndDate(
                durationValue === "custom" && !dateRange?.[0] && !dateRange?.[1]
                    ? "monthly"
                    : durationValue,
                dateRange
            )
        );
    }, [durationValue, dateRange]);

    const handleClearSelections = () => {
        activeOrderTab === 0
            ? setAllSelectedSales([])
            : activeOrderTab === 1
            ? setOngoingSelectedSales([])
            : activeOrderTab === 2
            ? setFulfilledSelectedSales([])
            : setPendingSelectedSales([]);
    };

    return (
        <div>
            <TabsContainer
                tabs={tabs}
                activeTab={activeOrderTab}
                setActiveTab={(idx) => {
                    setActiveOrderTab(idx);
                    setUpdatedPage(1);
                    navigate(`/dashboard/order?currTab=${1}&currOrderTab=${idx}`, {
                        state: { internalNavigation: true },
                    });
                }}
                component={displayActiveOrderTab()}
                className="!px-0"
                itemClassName="!pb-2"
                borderLineClase={"!text-slate-100 w-[45%] max-lg:w-full mb-6"}
                tabRowClassname="!overflow-visible"
                showButton={
                    <div className=" flex items-center space-x-2">
                        <TextInput
                            name={"search"}
                            value={
                                activeOrderTab === 0
                                    ? search
                                    : activeOrderTab === 1
                                    ? ongoingSearch
                                    : activeOrderTab === 2
                                    ? fulfilledSearch
                                    : pendingSearch
                            }
                            type={"text"}
                            inputPlaceholder={"Search "}
                            inputClassName={"!h-[38px] !w-[150px] pl-[0px] !bg-white text-sm !mb-0"}
                            onChange={handleSearch}
                            onInput={handleDebouncedChange}
                            leftIcon={
                                <i className="ri-search-line before:content-['\f0d1] text-slate-400 mt-[2px]"></i>
                            }
                            inputContainerClassname={
                                "!rounded-[4px]  max-sm:!w-full !pl-[40px] !h-[40px] !bg-white shadow-[0px_1px_2px_0px_#5B687126] !border !border-n-20"
                            }
                        />

                        <Dropdown
                            handleChange={(name, value) => {
                                setSaleType(value);
                                updateSearchParams({
                                    salesType: value,
                                });
                                if (value?.toLowerCase() === "dtc") {
                                    setCustomerEmail("");
                                    setCustomerName("");
                                    setCustomerSearch("");
                                    setDebounceSearch("");
                                }
                            }}
                            dropdown={
                                <div className="flex items-center justify-between whitespace-nowrap h-[40px]  py-2 px-4 space-x-1 rounded-[5px] border border-slate-200">
                                    <div className="flex items-center space-x-1">
                                        {/* <i className="ri-map-pin-line text-sm text-g-75 "></i> */}
                                        <span
                                            className={`text-g-75 font-rocGroteskMedium text-[13px]`}
                                        >
                                            {truncate(saleType, { length: 16 }) || "Sales type"}
                                        </span>
                                    </div>
                                    <div className="flex items-center space-x-1">
                                        {Boolean(saleType) && (
                                            <i
                                                onClick={() => {
                                                    setSaleType("");
                                                    updateSearchParams({
                                                        salesType: "",
                                                        customerEmail: "",
                                                        customerName: "",
                                                    });
                                                }}
                                                className="ri-close-circle-line text-slate-500"
                                            ></i>
                                        )}
                                        <i className="ri-arrow-down-s-line text-g-75"></i>
                                    </div>
                                </div>
                            }
                            // dropdownClassName=""
                            wholeContainerClass="!w-[33.33%] max-sm:!w-full"
                            dropdownContainerClasses={`shadow-cardShadow-4 !w-full border border-slate-100 right-0 rounded !max-h-fit !pt-0`}
                            dropdownOptions={[
                                ...["DTC", "Retail"]?.map((item) => {
                                    return {
                                        label: item,
                                        value: item,
                                    };
                                }),
                            ]}
                        />
                        {saleType?.toLowerCase() === "retail" && (
                            <Dropdown
                                handleChange={(name, value) => {
                                    const email = value?.split("{{}}")?.[1];
                                    const cusName = value?.split("{{}}")?.[0];
                                    setCustomerEmail(email);
                                    setCustomerName(cusName);

                                    updateSearchParams({
                                        customerEmail: email,
                                        customerName: cusName,
                                    });
                                }}
                                dropdown={
                                    <div className="flex items-center justify-between whitespace-nowrap h-[40px]  py-2 px-4 space-x-1 rounded-[5px] border border-slate-200">
                                        <div className="flex items-center space-x-1">
                                            {/* <i className="ri-map-pin-line text-sm text-g-75 "></i> */}
                                            <span
                                                className={`text-g-75 font-rocGroteskMedium text-[13px] capitalize`}
                                            >
                                                {truncate(customerName, { length: 12 }) ||
                                                    "Customer list"}
                                            </span>
                                        </div>
                                        <div className="flex items-center space-x-1">
                                            {Boolean(customerEmail || customerName) && (
                                                <i
                                                    onClick={() => {
                                                        setCustomerEmail("");
                                                        setCustomerSearch("");
                                                        setDebounceSearch("");
                                                        setCustomerName("");
                                                        updateSearchParams({
                                                            customerEmail: "",
                                                            customerName: "",
                                                        });
                                                    }}
                                                    className="ri-close-circle-line text-slate-500"
                                                ></i>
                                            )}
                                            <i className="ri-arrow-down-s-line text-g-75"></i>
                                        </div>
                                    </div>
                                }
                                search={
                                    <div>
                                        <TextInput
                                            value={customerSearch}
                                            name={"search"}
                                            type={"text"}
                                            required={false}
                                            onChange={(e) => setCustomerSearch(e.target.value)}
                                            onInput={debounce((e) => {
                                                setDebounceSearch(e.target.value);
                                            }, 800)}
                                            inputPlaceholder="Search customer list"
                                            containerClassname=""
                                            // inputContainerClassname={"!border-none !rounded-none "}
                                            inputClassName="w-full"
                                        />
                                    </div>
                                }
                                dropdownClassName=""
                                wholeContainerClass="!w-[33.33%] max-sm:!w-full"
                                id="modalContent"
                                dropdownContainerClasses={`shadow-cardShadow-4 !w-full border border-slate-100 right-0 rounded !pt-0 !max-h-[400px] overflow-y-scroll`}
                                dropdownOptions={[
                                    ...retailCustomerData?.map((item) => {
                                        return {
                                            label: item?.name,
                                            value: item?.name + "{{}}" + item?.email,
                                        };
                                    }),
                                ]}
                            />
                        )}
                        <Dropdown
                            handleChange={(name, value) => {
                                setChannelFilter(value);
                                updateSearchParams({
                                    salesChannel: value,
                                });
                            }}
                            dropdown={
                                <div className="flex items-center justify-between whitespace-nowrap h-[40px]  py-2 px-4 space-x-1 rounded-[5px] border border-slate-200">
                                    <div className="flex items-center space-x-1">
                                        {/* <i className="ri-map-pin-line text-sm text-g-75 "></i> */}
                                        <span
                                            className={`text-g-75 font-rocGroteskMedium text-[13px]`}
                                        >
                                            {truncate(channelFilter, { length: 16 }) ||
                                                "Sales channel"}
                                        </span>
                                    </div>
                                    <div className="flex items-center space-x-1">
                                        {Boolean(channelFilter) && (
                                            <i
                                                onClick={() => {
                                                    setChannelFilter("");
                                                    updateSearchParams({
                                                        salesChannel: "",
                                                    });
                                                }}
                                                className="ri-close-circle-line text-slate-500"
                                            ></i>
                                        )}
                                        <i className="ri-arrow-down-s-line text-g-75"></i>
                                    </div>
                                </div>
                            }
                            wholeContainerClass="!w-[33.33%] max-sm:!w-full"
                            id="modalContent"
                            dropdownContainerClasses={`shadow-cardShadow-4 !w-full border border-slate-100 right-0 rounded !max-h-fit !pt-0`}
                            dropdownOptions={[
                                ...salesChannel?.map((item) => {
                                    return {
                                        label: item,
                                        value: item,
                                    };
                                }),
                            ]}
                        />

                        <Dropdown
                            handleChange={(name, value) => {
                                console.log("first", name, value);
                            }}
                            dropdown={
                                <div className="flex items-center justify-between whitespace-nowrap h-[40px]  py-2 px-4 space-x-1 rounded-[5px] border border-slate-200">
                                    <div className="flex items-center space-x-1">
                                        {/* <i className="ri-map-pin-line text-sm text-g-75 "></i> */}
                                        <span
                                            className={`text-g-75 font-rocGroteskMedium text-[13px]`}
                                        >
                                            {truncate(
                                                activeOrderTab === 0
                                                    ? warehousefilter?.name
                                                    : activeOrderTab === 1
                                                    ? ongoingWarehousefilter?.name
                                                    : activeOrderTab === 2
                                                    ? fulfilledWarehousefilter?.name
                                                    : pendingWarehousefilter?.name,
                                                { length: 16 }
                                            ) || "Locations"}
                                        </span>
                                    </div>
                                    <div className="flex items-center space-x-1">
                                        {Boolean(
                                            activeOrderTab === 0
                                                ? warehousefilter?.name
                                                : activeOrderTab === 1
                                                ? ongoingWarehousefilter?.name
                                                : activeOrderTab === 2
                                                ? fulfilledWarehousefilter?.name
                                                : pendingWarehousefilter?.name
                                        ) && (
                                            <i
                                                onClick={() => {
                                                    clearWarehouseFilter();
                                                    handleClearFilter();
                                                }}
                                                className="ri-close-circle-line text-slate-500"
                                            ></i>
                                        )}
                                        <i className="ri-arrow-down-s-line text-g-75"></i>
                                    </div>
                                </div>
                            }
                            search={
                                <div>
                                    <TextInput
                                        value={warehouseSearch}
                                        name={"search"}
                                        type={"text"}
                                        required={false}
                                        onChange={handleWarehouseSearch}
                                        onInput={debounceChange}
                                        inputPlaceholder="Search warehouse"
                                        containerClassname=""
                                        // inputContainerClassname={"!border-none !rounded-none "}
                                        inputClassName="w-full"
                                    />
                                </div>
                            }
                            // dropdownClassName=""
                            wholeContainerClass="!w-[33.33%] max-sm:!w-full"
                            id="modalContent"
                            dropdownContainerClasses={`shadow-cardShadow-4 !w-full border border-slate-100 right-0 rounded !max-h-fit !pt-0`}
                            dropdownOptions={[
                                {
                                    customChild: (
                                        <div>
                                            <div className="max-h-[280px]">
                                                {allWarehouses?.map((warehouse) => {
                                                    return (
                                                        <div
                                                            key={warehouse?._id}
                                                            onClick={() => {
                                                                handleWarehouseFilterChange(
                                                                    warehouse?.warehouseName,
                                                                    warehouse?._id
                                                                );

                                                                handleFilterChange(
                                                                    warehouse?.warehouseName,
                                                                    warehouse?._id
                                                                );
                                                            }}
                                                        >
                                                            <div
                                                                className={`flex items-center space-x-3 px-3 py-2 hover:bg-slate-100 cursor-pointer ${
                                                                    warehouseValue?.value ===
                                                                        warehouse?._id &&
                                                                    "bg-slate-100"
                                                                }`}
                                                            >
                                                                <i className="ri-map-pin-line text-base text-g-75 "></i>
                                                                <div>
                                                                    <span
                                                                        className={`block text-g-75 text-sm font-rocGroteskMedium ${
                                                                            warehouse?.address
                                                                                ?.address &&
                                                                            "mb-[2px]"
                                                                        }`}
                                                                    >
                                                                        {truncate(
                                                                            warehouse?.warehouseName,
                                                                            { length: 40 }
                                                                        )}
                                                                    </span>
                                                                    <span className="block text-g-75 text-sm font-rocGroteskMedium ">
                                                                        {truncate(
                                                                            warehouse?.address
                                                                                ?.address,
                                                                            { length: 40 }
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ),
                                },
                            ]}
                        />
                    </div>
                }
            />

            {((allSelectedSales?.length > 0 && activeOrderTab === 0) ||
                (ongoingSelectedSales?.length > 0 && activeOrderTab === 1) ||
                (fulfilledSelectedSales?.length > 0 && activeOrderTab === 2) ||
                (pendingSelectedSales?.length > 0 && activeOrderTab === 3)) &&
                !singleUpdate && (
                    <div className="fixed bottom-[20px] left-1/2 -translate-x-1/2 flex items-center justify-cent er  bg-white border border-slate-200 rounded-lg p-2.5 gap-2 w-fit">
                        <p className="text-sm text-g-75 bg-y-10 px-2 py-0.5 rounded  font-rocGroteskMedium">
                            {activeOrderTab === 0
                                ? allSelectedSales?.length
                                : activeOrderTab === 1
                                ? ongoingSelectedSales?.length
                                : activeOrderTab === 2
                                ? fulfilledSelectedSales?.length
                                : pendingSelectedSales?.length}{" "}
                        </p>
                        <p className="text-sm text-gm-50 font-rocGroteskMedium">
                            Sales order selected
                        </p>

                        <div className="">
                            {updateCheck[activeOrderTab]?.tab === activeOrderTab &&
                            updateCheck[activeOrderTab]?.channels?.length > 0 ? (
                                <SynthTooltip
                                    content={`You can't change the status of any sales orders from ${updateCheck[
                                        activeOrderTab
                                    ]?.channels?.join(
                                        " or "
                                    )} within Synth. To update their status, make changes directly in ${updateCheck[
                                        activeOrderTab
                                    ]?.channels?.join(
                                        " or "
                                    )}. Synth will automatically sync those updates. To proceed with status changes here, uncheck those sales orders.`}
                                    divId={"change-status"}
                                    styling={{
                                        backgroundColor: "#142837",
                                        color: "#fff",
                                        borderRadius: "4px",
                                        opacity: "1",
                                        width: "378px",
                                    }}
                                    icon={
                                        <Button
                                            btnText="Update status"
                                            btnClassname="!px-3 !py-[6px] !w-fit !text-[13px] !rounded-[4px] !text-[#000] !bg-n-20 !border-none"
                                        />
                                    }
                                />
                            ) : (
                                <Button
                                    btnText="Update status"
                                    btnPermission={[
                                        "all_access",
                                        "create_sales_order",
                                        "update_sales_order",
                                    ]}
                                    btnClassname="!px-3 !py-[6px] !w-fit !text-[13px] !rounded-[4px] !text-[#000] !bg-n-20 !border-n-20"
                                    onClick={() => {
                                        setUpdateStatusModal(true);
                                    }}
                                />
                            )}
                        </div>

                        <div className="">
                            <SynthTooltip
                                content={`This shows you the summary of all the similar items within multiple orders in a single view.`}
                                divId={"order-summary"}
                                styling={{
                                    backgroundColor: "#142837",
                                    color: "#fff",
                                    borderRadius: "4px",
                                    opacity: "1",
                                    width: "258px",
                                }}
                                icon={
                                    <Button
                                        btnText="View order summary"
                                        btnPermission={[
                                            "all_access",
                                            "create_sales_order",
                                            "update_sales_order",
                                            "view_sales_order",
                                        ]}
                                        btnClassname="!px-3 !py-1.5 !w-fit !text-[13px] !border-none !rounded-[4px] !text-[#000]"
                                        btnType="textFirst"
                                        icon={<i className="ri-eye-line !text-[#000]"></i>}
                                        onClick={() => {
                                            setSelectedOrders(
                                                activeOrderTab === 0
                                                    ? allSelectedSales
                                                    : activeOrderTab === 1
                                                    ? ongoingSelectedSales
                                                    : activeOrderTab === 2
                                                    ? fulfilledSelectedSales
                                                    : pendingSelectedSales
                                            );
                                            setShowSummary(true);
                                        }}
                                    />
                                }
                            />
                        </div>

                        <div>
                            <Button
                                btnText="Delete sales orders"
                                btnPermission={[
                                    "all_access",
                                    "create_sales_order",
                                    "update_sales_order",
                                    "view_sales_order",
                                ]}
                                btnClassname="!px-3 !py-1.5 !w-fit !text-[13px] !border-none !rounded-[4px] !text-[#E4281D] !bg-[#FBF3F1]"
                                btnType="textFirst"
                                onClick={toggleSalesOrderModal}
                            />
                        </div>

                        <i
                            className="ri-close-fill text-[#64748B] text-lg font-bold ml-1 cursor-pointer"
                            onClick={() => {
                                setUpdateStatusModal(false);
                                handleClearSelections();
                            }}
                        ></i>
                    </div>
                )}

            <ModalContainer
                open={updateStatusModal}
                modalClassName={"py-10 px-10"}
                tailwindClassName="w-[500px] "
                showCloseIcon={false}
                closeModal={() => {
                    setUpdateStatusModal(false);
                    setStatus("");
                }}
            >
                <form
                    className="bg-white rounded-[8px] relative font-rocGroteskMedium"
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleUpdateStatus(
                            activeOrderTab === 0
                                ? "all"
                                : activeOrderTab === 1
                                ? "ongoing"
                                : activeOrderTab === 2
                                ? "fulfilled"
                                : "pending"
                        );
                    }}
                >
                    <div className="mt-4 ">
                        <div className="px-6 font-rocGroteskMedium border-b border-b-slate-200 flex items-center justify-between py-3">
                            <p className="mt-1 text-base text-gm-50 ">
                                Update{" "}
                                {activeOrderTab === 0
                                    ? allSelectedSales?.length
                                    : activeOrderTab === 1
                                    ? ongoingSelectedSales?.length
                                    : activeOrderTab === 2
                                    ? fulfilledSelectedSales?.length
                                    : pendingSelectedSales?.length}{" "}
                                sales order(s) status
                            </p>
                            <i
                                className={` cursor-pointer ri-close-fill text-gm-50 `}
                                onClick={() => {
                                    setUpdateStatusModal(false);
                                    setStatus("");
                                }}
                            ></i>
                        </div>
                        <div className="px-6 py-6  pb-12 space-y-4">
                            <SelectInput
                                value={status}
                                name={status}
                                placeholder={"Select status"}
                                handleChange={(name, value) => {
                                    setStatus(value);
                                }}
                                isRequired={true}
                                dropdownOptions={[
                                    ...(activeOrderTab !== 1
                                        ? [
                                              {
                                                  label: "Ongoing",
                                                  value: "ongoing",
                                                  icon: (
                                                      <div className="w-2 h-2 bg-[#0F69FE] rounded-full"></div>
                                                  ),
                                              },
                                          ]
                                        : []),
                                    ...(activeOrderTab !== 2
                                        ? [
                                              {
                                                  label: "Fulfilled",
                                                  value: "fulfilled",
                                                  icon: (
                                                      <div className="w-2 h-2 bg-[#22C55E] rounded-full"></div>
                                                  ),
                                              },
                                          ]
                                        : []),
                                    ...(activeOrderTab !== 3
                                        ? [
                                              {
                                                  label: "Pending",
                                                  value: "pending",
                                                  icon: (
                                                      <div className="w-2 h-2 bg-[#EAB308] rounded-full"></div>
                                                  ),
                                              },
                                          ]
                                        : []),
                                ]}
                                dropdownClassName="!max-h-24"
                            />
                        </div>

                        <div className="flex items-center  justify-end gap-2  border-t border-t-slate-200 py-4 px-6">
                            <Button
                                btnText={"Cancel"}
                                btnClassname="!py-3 cursor-pointer !text-[14px] !bg-n-20 !text-gm-50 !border border-n-20 !font-rocGroteskMedium !px-4 !w-fit"
                                onClick={() => {
                                    setUpdateStatusModal(false);
                                    setStatus("");
                                }}
                                disabled={isUpdating}
                            />
                            <Button
                                type="submit"
                                btnText="Update status"
                                isLoading={isUpdating}
                                disabled={isUpdating}
                                btnClassname="!py-3 cursor-pointer !text-[14px] !text-gm-50 !border border-n-20 !font-rocGroteskMedium !px-4 !w-fit"
                            />
                        </div>
                    </div>
                </form>
            </ModalContainer>

            <SalesOrderDelete
                open={deleteSalesOrderModal}
                onClose={toggleSalesOrderModal}
                salesOrderIds={[
                    ...(activeOrderTab === 0 ? allSelectedSales?.map((sale) => sale?._id) : []),
                    ...(activeOrderTab === 1 ? ongoingSelectedSales?.map((sale) => sale?._id) : []),
                    ...(activeOrderTab === 2
                        ? fulfilledSelectedSales?.map((sale) => sale?._id)
                        : []),
                    ...(activeOrderTab === 3 ? pendingSelectedSales?.map((sale) => sale?._id) : []),
                ]}
            />
        </div>
    );
};

export default OrderList;
